// frontend/packages/business-console/src/hooks/useDirectUpload.js
//
// Direct file upload to Cloudinary without the widget UI.
// Two-step signing flow (matches how the widget works):
//   1. Get upload intent from backend (public_id, folder, tags, etc.)
//   2. Sign the exact params we're sending via the signature endpoint
//   3. POST to Cloudinary with the fresh signature

import { useState, useCallback } from 'react';
import {
  createCloudinaryUploadIntent,
  createMediaAssetFromCloudinary,
  getCloudinaryUploadSignature,
} from '../services/mediaService';
import { toCldToken } from '../utils/mediaToken';

export default function useDirectUpload() {
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(async (file) => {
    if (!file) throw new Error('No file provided');

    setUploading(true);
    try {
      const isVideo = file.type?.startsWith('video');
      const resourceType = isVideo ? 'video' : 'image';

      const intent = await createCloudinaryUploadIntent({
        resourceType,
        filename: file.name,
        bytes: file.size,
      });

      if (!intent?.cloudName || !intent?.apiKey || !intent?.params?.public_id) {
        throw new Error('Invalid upload intent from server');
      }

      const timestamp = Math.floor(Date.now() / 1000);

      const paramsToSign = {
        public_id: intent.params.public_id,
        timestamp: String(timestamp),
      };
      if (intent.params.tags) paramsToSign.tags = intent.params.tags;
      if (intent.params.folder) paramsToSign.folder = intent.params.folder;
      if (intent.params.upload_preset)
        paramsToSign.upload_preset = intent.params.upload_preset;

      const sigResult = await getCloudinaryUploadSignature(paramsToSign);
      const signature = sigResult?.signature;

      if (!signature) {
        throw new Error('Failed to get upload signature');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', intent.apiKey);
      formData.append('timestamp', String(timestamp));
      formData.append('signature', signature);
      formData.append('public_id', intent.params.public_id);
      if (intent.params.tags) formData.append('tags', intent.params.tags);
      if (intent.params.folder) formData.append('folder', intent.params.folder);
      if (intent.params.upload_preset)
        formData.append('upload_preset', intent.params.upload_preset);

      const resp = await fetch(
        `https://api.cloudinary.com/v1_1/${intent.cloudName}/${resourceType}/upload`,
        { method: 'POST', body: formData }
      );

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(
          `Upload failed: ${resp.status} ${errData?.error?.message || ''}`
        );
      }

      const data = await resp.json();

      // Register the upload as a MediaAsset, the same way the library page and
      // the picker do after their widget uploads. Without this the file lived
      // only in Cloudinary and as a `cld:` token inside one document: it never
      // appeared in Media, could not be reused on another page, never showed
      // up in the "needs alt text" count, and could not be given alt text at
      // all. createMediaAssetFromCloudinary is idempotent on
      // (businessId, public_id), so a later Cloudinary webhook is a no-op.
      //
      // Best-effort on purpose: the upload itself has already succeeded and
      // the token works, so a registration failure must not fail the upload
      // and lose the operator's file. It costs library visibility, not the
      // image.
      let asset = null;
      try {
        asset = await createMediaAssetFromCloudinary(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[useDirectUpload] media asset registration failed:', err);
      }

      return {
        public_id: data.public_id,
        secure_url: data.secure_url,
        resource_type: data.resource_type,
        format: data.format,
        width: data.width,
        height: data.height,
        bytes: data.bytes,
        token: toCldToken(data.public_id),
        asset,
        registered: Boolean(asset),
      };
    } finally {
      setUploading(false);
    }
  }, []);

  return { upload, uploading };
}
